import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, ZAR, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import {
  Users, Clock, CheckCircle2, TrendingUp, LogOut, ExternalLink, ShieldCheck, XCircle, FileText, IdCard,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const statusStyles = {
  approved: "text-green-400 border-green-400/40 bg-green-400/10",
  pending: "text-yellow-400 border-yellow-400/40 bg-yellow-400/10",
  rejected: "text-primary border-primary/40 bg-primary/10",
  unverified: "text-muted-foreground border-border bg-secondary",
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    const [s, st] = await Promise.all([api.get("/admin/stats"), api.get(`/admin/students${q}`)]);
    setStats(s.data.stats);
    setStudents(st.data.students);
  };
  useEffect(() => { load(); }, [filter]);

  const verify = async (id, action, rsn) => {
    setBusy(true);
    try {
      await api.post(`/admin/students/${id}/verify`, { action, reason: rsn });
      toast.success(action === "approve" ? "Student approved" : "Student rejected");
      setRejectFor(null);
      setReason("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => { await logout(); navigate("/login"); };

  const statCards = stats
    ? [
        [Users, "Students", stats.students, "stat-students"],
        [Clock, "Pending review", stats.pending, "stat-pending"],
        [CheckCircle2, "Approved", stats.approved, "stat-approved"],
        [TrendingUp, "Total credit used", ZAR(stats.total_credit_used), "stat-used"],
      ]
    : [];

  return (
    <div className="min-h-screen bg-background relative z-10">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="text-xs font-medium tracking-overline uppercase text-primary border border-primary/40 px-2 py-1">Admin</span>
          </div>
          <button onClick={onLogout} data-testid="admin-logout-btn"
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-border hover:border-primary hover:text-primary transition-colors duration-150">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <p className="text-xs font-medium tracking-overline text-primary uppercase mb-2">Console</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8">Overview</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {statCards.map(([Icon, label, val, tid], i) => (
            <motion.div key={tid} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }} className="border border-border p-5" data-testid={tid}>
              <Icon className="text-primary mb-3" size={20} />
              <p className="font-mono text-2xl font-bold">{val}</p>
              <p className="text-xs text-muted-foreground mt-1">{label}</p>
            </motion.div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <h2 className="text-lg font-bold tracking-tight">Students</h2>
          <div className="flex gap-2 flex-wrap">
            {["all", "pending", "approved", "rejected"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`}
                className={`px-3 py-1.5 text-sm font-medium border capitalize transition-colors duration-150 ${
                  filter === f ? "border-primary bg-primary text-white" : "border-border hover:border-primary"
                }`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="border border-border divide-y divide-border" data-testid="admin-students-list">
          {students.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">No students found.</div>
          )}
          {students.map((s) => {
            const st = s.verification_status || "unverified";
            const docs = s.documents || {};
            return (
              <div key={s.id} className="p-5" data-testid={`admin-student-${s.id}`}>
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="w-11 h-11 overflow-hidden bg-secondary flex items-center justify-center shrink-0">
                    {s.photo_path ? (
                      <img src={fileUrl(s.photo_path)} alt="" className="w-full h-full object-cover" />
                    ) : <Users size={18} className="text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{s.name}</p>
                    <p className="text-xs text-muted-foreground font-mono truncate">{s.email}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {s.institution || "—"} · #{s.student_number || "—"}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block text-[10px] font-medium tracking-overline uppercase border px-2 py-1 ${statusStyles[st]}`} data-testid={`admin-status-${s.id}`}>
                      {st}
                    </span>
                    <p className="text-xs text-muted-foreground font-mono mt-2">Used {ZAR(s.credit_used)}</p>
                    <p className="text-xs text-muted-foreground font-mono">{s.points} pts</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap mt-4">
                  {docs.proof_of_registration ? (
                    <a href={fileUrl(docs.proof_of_registration.path)} target="_blank" rel="noreferrer"
                      data-testid={`admin-doc-por-${s.id}`}
                      className="text-xs text-primary inline-flex items-center gap-1">
                      <FileText size={13} /> Proof of registration <ExternalLink size={11} />
                    </a>
                  ) : <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><FileText size={13} /> No registration doc</span>}
                  {docs.id_copy ? (
                    <a href={fileUrl(docs.id_copy.path)} target="_blank" rel="noreferrer"
                      data-testid={`admin-doc-id-${s.id}`}
                      className="text-xs text-primary inline-flex items-center gap-1">
                      <IdCard size={13} /> ID copy <ExternalLink size={11} />
                    </a>
                  ) : <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><IdCard size={13} /> No ID copy</span>}

                  <div className="ml-auto flex gap-2">
                    <button onClick={() => verify(s.id, "approve")} disabled={busy || st === "approved"}
                      data-testid={`admin-approve-${s.id}`}
                      className="px-3 py-1.5 text-sm font-medium bg-green-500 text-black inline-flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform duration-150 disabled:opacity-40 disabled:hover:translate-y-0">
                      <ShieldCheck size={14} /> Approve
                    </button>
                    <button onClick={() => { setRejectFor(s); setReason(""); }} disabled={busy || st === "rejected"}
                      data-testid={`admin-reject-${s.id}`}
                      className="px-3 py-1.5 text-sm font-medium border border-primary text-primary inline-flex items-center gap-1.5 hover:bg-primary hover:text-white transition-colors duration-150 disabled:opacity-40">
                      <XCircle size={14} /> Reject
                    </button>
                  </div>
                </div>
                {st === "rejected" && s.rejection_reason && (
                  <p className="text-xs text-primary mt-2">Reason: {s.rejection_reason}</p>
                )}
              </div>
            );
          })}
        </div>
      </main>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Reject verification</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Let {rejectFor?.name} know why their documents were rejected.
          </p>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Proof of registration is unclear / expired"
            data-testid="admin-reject-reason"
            className="w-full bg-secondary border border-border px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-primary min-h-[90px]"
          />
          <DialogFooter>
            <button onClick={() => verify(rejectFor.id, "reject", reason)} disabled={busy}
              data-testid="admin-reject-confirm"
              className="bg-primary text-white font-medium px-5 py-2.5 disabled:opacity-60">
              Confirm rejection
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
